import { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import api from '../../hooks/useApi';
import { useApp } from '../../context';
import { formatMoney, formatDate } from '../../utils/format';
import { canAccess } from '../../utils/rbac';
import StatusFilter from '../shared/StatusFilter';
import Tag from '../shared/Tag';
import Modal from '../shared/Modal';
import { useToast } from '../ui/Toast';
import { sesExpected } from '../../utils/milestoneStatus';
import { toCsv } from '../../utils/csv.js';
import MilestoneReportingTab from './MilestoneReportingTab';
import { filterMilestones, REPORT_CSV_HEADERS, reportCsvRows, groupDateChanges } from '../../utils/milestoneReport';

// Labels + accent colours for the running sum card, keyed by the active filter.
const FILTER_LABELS = {
  Awaiting: 'Awaiting', 'Ready to bill': 'Ready to bill', Invoiced: 'Invoiced',
  Paid: 'Paid', Cancelled: 'Cancelled', 'Credit Noted': 'Credit Noted',
  All: 'All', Overdue: 'Overdue', Due30: 'Due ≤ 30d', OnTrack: 'On Track',
};
const FILTER_SUM_COLOR = {
  Awaiting: 'text-amber-500', 'Ready to bill': 'text-amber-500', Invoiced: 'text-blue-500',
  Paid: 'text-emerald-500', Overdue: 'text-red-500', Due30: 'text-amber-500',
};

function addDays(dateStr, days) {
  if (!dateStr || !days) return null;
  const d = new Date(dateStr);
  d.setDate(d.getDate() + parseInt(days, 10));
  return d.toISOString().split('T')[0];
}

function getToday() {
  return new Date().toISOString().split('T')[0];
}

function get7DaysFromNow() {
  const d = new Date();
  d.setDate(d.getDate() + 7);
  return d.toISOString().split('T')[0];
}

// Milestone values are SOW money, the sow:see-financials grant; the API leaves
// them out for anybody else (pen test AD26341, AD12/AD16), so the amount column,
// totals and invoice amounts are not offered either.
function downloadCSV(rows) {
  const headers = ['SOW', 'SOW Name', 'Client', 'Client PO', 'Milestone', 'Status', 'Contract Date', 'Forecast Date', 'Description', 'Amount (excl VAT)', 'PM'];
  const data = rows.map(m => [
    m.reference || '',
    m.sow_detail || '',
    m.client_name || '',
    m.sow_po_number || '',
    m.milestone_code || '',
    m.status || '',
    m.contract_date || '',
    m.forecast_date || '',
    m.description || '',
    m.invoice_amount_excl != null ? m.invoice_amount_excl : '',
    m.pm_name || '',
  ]);
  if (!canAccess('sow:see-financials')) {
    const amount = headers.indexOf('Amount (excl VAT)');
    const without = (row) => row.filter((_, i) => i !== amount);
    saveCsv(toCsv(without(headers), data.map(without)), `milestones-${getToday()}.csv`);
    return;
  }
  saveCsv(toCsv(headers, data), `milestones-${getToday()}.csv`);
}

function saveCsv(csv, filename) {
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

const PAGE_SIZE = 25;

const SORT_COLS = {
  reference:    m => m.reference || '',
  milestone_code: m => m.milestone_code || '',
  status:       m => m.status || '',
  contract_date: m => m.contract_date || '',
  forecast_date: m => m.forecast_date || '',
  description:  m => m.description || '',
  invoice_amount_excl: m => m.invoice_amount_excl ?? 0,
  pm_name:      m => m.pm_name || '',
};

function SowRefCell({ m }) {
  const { navigate } = useApp();
  const [tip, setTip] = useState(false);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const timerRef = useRef(null);
  const tipRef = useRef(null);

  const showTip = (e) => {
    timerRef.current = setTimeout(() => {
      setPos({ x: e.clientX + 10, y: e.clientY + 10 });
      setTip(true);
    }, 400);
  };
  const hideTip = () => { clearTimeout(timerRef.current); setTip(false); };

  // The card hangs off the cursor with position: fixed, so on a row low down or
  // far right it would open past the edge of the window. Measure it once
  // rendered and flip it back over the cursor where it does not fit. This runs
  // before paint, so the card never appears in the wrong place.
  //
  // It only lines up with the cursor at all because the card is portalled to
  // document.body below: App.jsx puts the display-density `zoom` on div.content,
  // and a zoomed element becomes the containing block for its fixed-position
  // descendants and scales their coordinates, which is what pushed the card
  // further off screen the lower down the page you hovered.
  useLayoutEffect(() => {
    const el = tipRef.current;
    if (!el) return;
    const { width, height } = el.getBoundingClientRect();
    const margin = 8;
    let left = pos.x;
    let top  = pos.y;
    if (left + width  > window.innerWidth  - margin) left = Math.max(margin, pos.x - width  - 20);
    if (top  + height > window.innerHeight - margin) top  = Math.max(margin, pos.y - height - 20);
    el.style.left = `${left}px`;
    el.style.top  = `${top}px`;
  }, [tip, pos]);

  return (
    <span style={{ position: 'relative', display: 'inline-block' }}>
      <button
        onClick={() => navigate('sow-detail', m.sow_id)}
        onMouseEnter={showTip}
        onMouseLeave={hideTip}
        onMouseMove={e => { if (tip) setPos({ x: e.clientX + 10, y: e.clientY + 10 }); }}
        className="text-[9px] font-semibold text-blue-600 hover:underline hover:text-blue-800 bg-transparent border-none cursor-pointer p-0 whitespace-nowrap"
      >
        {m.reference}
      </button>
      {/* The name of the SOW is what says which job this is; the client it
          belongs to is the hover card's job now that it has no column. */}
      <div
        className="text-[9px] text-gray-700 font-medium leading-tight max-w-[220px] truncate"
        onMouseEnter={showTip}
        onMouseLeave={hideTip}
        onMouseMove={e => { if (tip) setPos({ x: e.clientX + 10, y: e.clientY + 10 }); }}
      >
        {m.sow_detail}
      </div>
      <div className={`text-[7px] mt-0.5 whitespace-nowrap ${m.sow_po_number ? 'text-gray-400' : 'text-gray-300'}`}>
        {m.sow_po_number ? `PO: ${m.sow_po_number}` : 'No PO'}
      </div>
      {tip && (m.sow_detail || m.client_name) && createPortal(
        <div ref={tipRef} style={{
          position: 'fixed', left: pos.x, top: pos.y, zIndex: 9999,
          background: '#1e293b', color: '#f1f5f9', borderRadius: 6,
          padding: '7px 10px', fontSize: 10, maxWidth: 260,
          boxShadow: '0 4px 16px rgba(0,0,0,0.3)', pointerEvents: 'none',
          lineHeight: 1.5,
        }}>
          {m.client_name && <div className="font-semibold">{m.client_name}</div>}
          {m.client_contact_name && <div style={{ color: '#94a3b8' }}>Contact: {m.client_contact_name}</div>}
          {m.sow_detail && <div style={{ color: '#94a3b8' }}>{m.sow_detail}</div>}
        </div>,
        document.body,
      )}
    </span>
  );
}

// Deep links into a tab of this screen. The dashboard tiles use overdue/due30/ontrack;
// 'ready-to-bill' is the Ready to Bill notification, which asked to land on that section
// rather than on the default Awaiting list.
const DETAIL_FILTER_MAP = {
  overdue: 'Overdue', due30: 'Due30', ontrack: 'OnTrack',
  'ready-to-bill': 'Ready to bill',
};

export default function MilestonesPage() {
  const { toast } = useToast();
  const { detailId } = useApp();
  const [milestones, setMilestones] = useState([]);
  const [filter, setFilter] = useState(DETAIL_FILTER_MAP[detailId] || 'Awaiting');
  // Billing is the original list with its invoice actions; Reporting is the delivery view.
  const [view, setView] = useState('billing');
  // The Reporting tab's filters live here so Export CSV can send what that
  // table shows, and so they survive a switch to Billing and back.
  const [reportFilter, setReportFilter] = useState({ status: 'Awaiting', month: '', from: '', to: '', columns: {} });
  const [exporting, setExporting] = useState(false);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState({ col: 'forecast_date', dir: 'asc' });
  const [recordInvoice, setRecordInvoice] = useState(null);
  const [invoiceForm, setInvoiceForm] = useState({
    invoice_number: '', invoice_amount_excl: '', formal_invoice_date: '',
    invoice_amount_incl: '', ses_number: '',
  });
  const [markPaid, setMarkPaid] = useState(null);
  const [paidDate, setPaidDate] = useState('');
  // SES mandate: shown when moving to Ready to bill with ses_applicable=true and no ses_number
  const [sesPrompt, setSesPrompt]   = useState(null); // the milestone needing SES
  const [sesInput,  setSesInput]    = useState('');
  const seesMoney = canAccess('sow:see-financials');

  const load = () => { api.get('milestones', { active_only: 1 }).then(setMilestones); };
  useEffect(load, []);
  useEffect(() => { setPage(1); }, [filter, search]);

  const today       = getToday();
  const sevenDays   = get7DaysFromNow();
  const in30Days    = new Date(Date.now() + 30 * 86400000).toISOString().split('T')[0];
  const ACTIVE_ST   = new Set(['Awaiting', 'In Progress', 'Ready to bill']);

  const statusFiltered =
    filter === 'All'     ? milestones :
    // Overdue / Due ≤30d are about work still to be done — a milestone that's
    // already Ready to bill/In Progress-past-delivery or Invoiced shouldn't
    // show up as "overdue"/"due soon" here, only ones still Awaiting.
    filter === 'Overdue' ? milestones.filter(m => m.status === 'Awaiting' && m.forecast_date && m.forecast_date < today) :
    filter === 'Due30'   ? milestones.filter(m => m.status === 'Awaiting' && m.forecast_date && m.forecast_date >= today && m.forecast_date <= in30Days) :
    filter === 'OnTrack' ? milestones.filter(m => ACTIVE_ST.has(m.status) && (!m.forecast_date || m.forecast_date > in30Days)) :
    milestones.filter(m => m.status === filter);

  const q = search.trim().toLowerCase();
  const filtered = q
    ? statusFiltered.filter(m =>
        (m.reference      || '').toLowerCase().includes(q) ||
        (m.sow_detail     || '').toLowerCase().includes(q) ||
        (m.client_name    || '').toLowerCase().includes(q) ||
        (m.sow_po_number  || '').toLowerCase().includes(q) ||
        (m.milestone_code || '').toLowerCase().includes(q) ||
        (m.description    || '').toLowerCase().includes(q) ||
        (m.pm_name        || '').toLowerCase().includes(q)
      )
    : statusFiltered;

  const sortFn = SORT_COLS[sort.col] || (() => '');
  const sortedFiltered = [...filtered].sort((a, b) => {
    const av = sortFn(a);
    const bv = sortFn(b);
    if (av < bv) return sort.dir === 'asc' ? -1 : 1;
    if (av > bv) return sort.dir === 'asc' ? 1 : -1;
    return 0;
  });

  const totalPages = Math.max(1, Math.ceil(sortedFiltered.length / PAGE_SIZE));
  const safePage   = Math.min(page, totalPages);
  const pageRows   = sortedFiltered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const toggleSort = (col) => {
    setSort(prev =>
      prev.col === col
        ? { col, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { col, dir: 'asc' }
    );
  };

  const sortIcon = (col) => {
    if (sort.col !== col) return <span className="text-gray-300 ml-0.5">↕</span>;
    return <span className="text-gold ml-0.5">{sort.dir === 'asc' ? '↑' : '↓'}</span>;
  };

  const byStatus = (s) => milestones.filter(m => m.status === s);
  const sumAmount = (arr) => arr.reduce((s, m) => s + (m.invoice_amount_excl || 0), 0);

  const updateStatus = async (id, status) => {
    try {
      const res = await api.put(`milestones/${id}`, { status });
      if (res?.sow_auto_completed) toast.success(`${res.reference} completed — every milestone on it is now paid.`);
      if (res?.sow_awaiting_payment) toast.success(`${res.reference} is now Awaiting Payment: every milestone on it is invoiced or paid.`);
      if (res?.sow_reopened) toast.success(`${res.reference} is back in delivery: this milestone still has to be invoiced.`);
      load();
    } catch (e) {
      toast.error('Failed to update status: ' + e.message);
    }
  };

  // Called when the select or a button changes status — intercepts Ready to bill
  // if SES is applicable and not yet recorded
  const handleStatusChange = async (m, newStatus) => {
    if (newStatus === 'Ready to bill' && m.ses_applicable && !m.ses_number) {
      setSesPrompt(m);
      setSesInput('');
      return;
    }
    await updateStatus(m.id, newStatus);
  };

  const submitSesAndReadyToBill = async () => {
    if (!sesInput.trim()) return;
    try {
      await api.put(`milestones/${sesPrompt.id}`, {
        ses_number: sesInput.trim(),
        status: 'Ready to bill',
      });
      setSesPrompt(null);
      setSesInput('');
      load();
    } catch (e) {
      toast.error('Failed to save SES number: ' + e.message);
    }
  };

  const openRecordInvoice = (m) => {
    setInvoiceForm({
      invoice_number: m.invoice_number || '',
      invoice_amount_excl: m.invoice_amount_excl || '',
      formal_invoice_date: m.formal_invoice_date || new Date().toISOString().split('T')[0],
      invoice_amount_incl: m.invoice_amount_incl || '',
      ses_number: m.ses_number || '',
    });
    setRecordInvoice(m);
  };

  const submitInvoice = async () => {
    const m = recordInvoice;
    const paymentTerms = m.payment_terms || 30;
    const estPayDate = addDays(invoiceForm.formal_invoice_date, paymentTerms);
    try {
      const res = await api.put(`milestones/${m.id}`, {
        invoice_number:      invoiceForm.invoice_number,
        ...(seesMoney ? {
          invoice_amount_excl: parseFloat(invoiceForm.invoice_amount_excl) || null,
          invoice_amount_incl: parseFloat(invoiceForm.invoice_amount_incl) || null,
        } : {}),
        formal_invoice_date: invoiceForm.formal_invoice_date,
        ses_number:          invoiceForm.ses_number || null,
        est_pay_date:        estPayDate,
        status:              'Invoiced',
      });
      if (res?.sow_awaiting_payment) toast.success(`${res.reference} is now Awaiting Payment: every milestone on it is invoiced or paid.`);
      setRecordInvoice(null);
      load();
    } catch (e) {
      toast.error('Failed to record invoice: ' + e.message);
    }
  };

  const setF = (k, v) => setInvoiceForm(p => ({ ...p, [k]: v }));

  // The Reporting CSV carries each milestone's contract and forecast date
  // changes, which the table only loads row by row, so fetch them all first.
  // Without them the export would be missing its last column, so a failed fetch
  // stops the export rather than sending it short.
  const exportReporting = async () => {
    setExporting(true);
    try {
      const changes = groupDateChanges(await api.get('milestones/date-changes'));
      const rows = reportCsvRows(filterMilestones(milestones, reportFilter), changes);
      saveCsv(toCsv(REPORT_CSV_HEADERS, rows), `milestones-reporting-${getToday()}.csv`);
    } catch (e) {
      toast.error('Could not load the date changes for the export: ' + e.message);
    } finally {
      setExporting(false);
    }
  };

  const openMarkPaid = (m) => {
    setPaidDate(new Date().toISOString().split('T')[0]);
    setMarkPaid(m);
  };

  const submitMarkPaid = async () => {
    try {
      const res = await api.put(`milestones/${markPaid.id}`, {
        status: 'Paid',
        paid_date: paidDate,
      });
      if (res?.sow_auto_completed) toast.success(`${res.reference} completed — every milestone on it is now paid.`);
      if (res?.sow_awaiting_payment) toast.success(`${res.reference} is now Awaiting Payment: every milestone on it is invoiced or paid.`);
      if (res?.sow_reopened) toast.success(`${res.reference} is back in delivery: this milestone still has to be invoiced.`);
      setMarkPaid(null);
      load();
    } catch (e) {
      toast.error('Failed to mark as paid: ' + e.message);
    }
  };

  const previewPayDate = recordInvoice
    ? addDays(invoiceForm.formal_invoice_date, recordInvoice.payment_terms || 30)
    : null;

  // Whether the Record Invoice modal shows the SES number: the SOW is SES applicable or
  // the client PO is in the 5200 series, and also whenever a number was recorded anyway,
  // since one on record is needed to raise the invoice whatever the flags say.
  const sesRelevant = !!recordInvoice && (
    !!recordInvoice.ses_number ||
    sesExpected({ sesApplicable: recordInvoice.ses_applicable, poNumber: recordInvoice.sow_po_number })
  );

  // Sortable TH helper
  const SortTH = ({ col, label, right }) => (
    <th
      className={`px-2 py-1 text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50 cursor-pointer select-none hover:text-navy ${right ? 'text-right' : 'text-left'}`}
      onClick={() => toggleSort(col)}
    >
      {label}{sortIcon(col)}
    </th>
  );

  return (
    <div className="fade-in">
      <div className="flex items-center justify-between mb-2">
        <div>
          <h2 className="text-base font-extrabold text-navy">Milestones</h2>
          <div className="text-[9px] text-gray-500">
            {milestones.length} milestones · Active, On Hold &amp; Awaiting Payment SOWs, plus Ready to bill on Complete ones
            {q ? ` · ${sortedFiltered.length} matching` : ''}
          </div>
          <div className="text-[8px] text-gray-400 mt-0.5">
            Cancelled &amp; Expired SOWs are excluded. "Awaiting" + "Ready to bill" is the outstanding-to-invoice figure; it ties to the Tracker's "Remaining" once every SOW's milestones cover its contract total.
          </div>
        </div>
        <button
          onClick={() => (view === 'reporting' ? exportReporting() : downloadCSV(sortedFiltered))}
          disabled={exporting}
          title={view === 'reporting' ? 'Export the Reporting table as shown' : 'Export the Billing list as shown'}
          className="px-2 py-1 text-[9px] font-semibold border border-gray-200 rounded bg-white hover:bg-gray-50 text-gray-600 flex items-center gap-1 flex-shrink-0 whitespace-nowrap disabled:opacity-40"
        >
          {exporting ? 'Exporting…' : '↓ Export CSV'}
        </button>
      </div>
      <div className="flex border-b border-gray-200 mb-2">
        {[['billing', 'Billing'], ['reporting', 'Reporting']].map(([k, label]) => (
          <button
            key={k}
            className={`px-2 py-1.5 text-[9px] font-medium border-b-2 -mb-px whitespace-nowrap ${view === k ? 'text-gold border-gold font-semibold' : 'text-gray-400 border-transparent'}`}
            onClick={() => setView(k)}
          >{label}</button>
        ))}
      </div>

      {view === 'reporting' && (
        <MilestoneReportingTab
          milestones={milestones} filter={reportFilter} onFilterChange={setReportFilter}
          onMilestoneSaved={(id, patch) => setMilestones(ms => ms.map(m => (m.id === id ? { ...m, ...patch } : m)))}
        />
      )}

      {view === 'billing' && <>
      <div className="mb-2">
        <input
          className="form-input w-full max-w-xs"
          placeholder="Search SOW ref, name, client, PO, PM, description…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </div>

      <StatusFilter value={filter} onChange={setFilter} options={[
        { key: 'Awaiting',      label: 'Awaiting',      count: byStatus('Awaiting').length },
        { key: 'Ready to bill', label: 'Ready to bill', count: byStatus('Ready to bill').length },
        { key: 'Invoiced',      label: 'Invoiced',      count: byStatus('Invoiced').length },
        { key: 'Paid',          label: 'Paid',          count: byStatus('Paid').length },
        { key: 'Cancelled',     label: 'Cancelled',     count: byStatus('Cancelled').length },
        { key: 'Credit Noted',  label: 'Credit Noted',  count: byStatus('Credit Noted').length },
        { key: 'All',           label: 'All',           count: milestones.length },
        { key: 'Overdue',       label: '⚠ Overdue',     count: milestones.filter(m => m.status === 'Awaiting' && m.forecast_date && m.forecast_date < today).length },
        { key: 'Due30',         label: 'Due ≤ 30d',     count: milestones.filter(m => m.status === 'Awaiting' && m.forecast_date && m.forecast_date >= today && m.forecast_date <= in30Days).length },
        { key: 'OnTrack',       label: 'On Track',      count: milestones.filter(m => ACTIVE_ST.has(m.status) && (!m.forecast_date || m.forecast_date > in30Days)).length },
      ]} />

      {/* Sum reflects whatever tab/filter is currently selected (and any search) */}
      {seesMoney && <div className="mb-3">
        <div className="bg-white border border-gray-200 rounded p-2 inline-block min-w-[200px]">
          <div className="text-[8px] text-gray-500 uppercase font-bold">
            {FILTER_LABELS[filter] || filter} total · {filtered.length} milestone{filtered.length !== 1 ? 's' : ''}
          </div>
          <div className={`text-[11px] sm:text-base font-extrabold break-all leading-tight ${FILTER_SUM_COLOR[filter] || 'text-navy'}`}>
            {formatMoney(sumAmount(filtered))}
          </div>
        </div>
      </div>}

      <div className="bg-white border border-gray-200 rounded overflow-x-auto">
        <table className="w-full">
          <thead><tr>
            <SortTH col="reference"            label="SOW" />
            <SortTH col="milestone_code"       label="Milestone" />
            <SortTH col="status"               label="Status" />
            <SortTH col="contract_date"        label="Contract" />
            <SortTH col="forecast_date"        label="Forecast" />
            <th className="px-2 py-1 text-left text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">Description</th>
            {seesMoney && <SortTH col="invoice_amount_excl"  label="Amount" right />}
            <SortTH col="pm_name"              label="PM" />
            <th className="px-2 py-1 text-left text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">Actions</th>
          </tr></thead>
          <tbody>{pageRows.map(m => {
            const doneSt   = ['Ready to bill','Ready To Bill','Invoiced','Paid','Cancelled','Complete','Completed'];
            // Once a milestone is ready to bill, invoiced, paid, cancelled or complete the
            // work is effectively done — suppress all forecast overdue/slipped/due-soon flags.
            const inactive = doneSt.includes(m.status);
            const isMissed = m.forecast_date && m.forecast_date < today && !inactive;
            const isForecastPast = m.forecast_date && m.forecast_date < today && !inactive;
            const isForecastSoon = m.forecast_date && m.forecast_date >= today && m.forecast_date <= sevenDays && !inactive;
            const slipped  = !inactive && m.forecast_date && m.contract_date && m.forecast_date > m.contract_date;
            const isReadyToBill = m.status === 'Ready to bill';
            const isInvoiced = m.status === 'Invoiced';
            return (
              <tr key={m.id} className={`hover:bg-gold/5 ${isForecastPast ? 'bg-red-50/40' : isForecastSoon ? 'bg-amber-50/40' : ''}`}>
                <td className="px-2 py-1 whitespace-nowrap"><SowRefCell m={m} /></td>
                <td className="px-2 py-1 text-[10px] font-bold">{m.milestone_code}</td>
                <td className="px-2 py-1">
                  <Tag status={m.status} />
                  {(() => {
                    const d =
                      m.status === 'Ready to bill' ? m.ready_to_bill_date :
                      m.status === 'Invoiced'      ? m.formal_invoice_date :
                      m.status === 'Paid'          ? m.paid_date :
                      m.status === 'Credit Noted'  ? m.credit_noted_date : null;
                    return d ? <div className="text-[8px] text-gray-400 mt-0.5 whitespace-nowrap">{formatDate(d)}</div> : null;
                  })()}
                  {(m.status === 'Invoiced' || m.status === 'Paid') && m.invoice_number && (
                    <div className="text-[8px] text-gray-600 font-medium mt-0.5 whitespace-nowrap">{m.invoice_number}</div>
                  )}
                  {m.status === 'Paid' && m.formal_invoice_date && (
                    <div className="text-[8px] text-gray-400 mt-0.5 whitespace-nowrap">inv {formatDate(m.formal_invoice_date)}</div>
                  )}
                </td>
                <td className="px-2 py-1 text-[9px]">{formatDate(m.contract_date)}</td>
                <td className={`px-2 py-1 text-[9px] whitespace-nowrap ${isForecastPast ? 'text-red-600 font-bold' : (slipped || isForecastSoon) ? 'text-amber-600 font-semibold' : 'font-normal'}`}>
                  <div>{formatDate(m.forecast_date)}</div>
                  {isMissed
                    ? <div className="text-[8px] font-bold bg-red-600 text-white px-1 rounded inline-block mt-0.5" title={m.missed_milestone_reason ? `Reason: ${m.missed_milestone_reason}` : 'No reason recorded — open Project Health to update'}>Missed{m.missed_milestone_reason ? '' : ' ⚠'}</div>
                    : isForecastPast
                      ? <div className="text-[8px] mt-0.5">⚠ Overdue</div>
                      : slipped
                        ? <div className="text-[8px] mt-0.5">⚠ At Risk</div>
                        : isForecastSoon
                          ? <div className="text-[8px] mt-0.5">⚑ Due soon</div>
                          : null
                  }
                </td>
                <td className="px-2 py-1 text-[10px] font-medium">{m.description}</td>
                {seesMoney && <td className="px-2 py-1 text-[10px] font-semibold text-right">{formatMoney(m.invoice_amount_excl)}</td>}
                <td className="px-2 py-1 text-[9px]">{m.pm_name || '-'}</td>
                <td className="px-2 py-1 whitespace-nowrap">
                  {isReadyToBill ? (
                    <div className="flex items-center gap-1 flex-wrap">
                      <button
                        onClick={() => openRecordInvoice(m)}
                        className="px-2 py-0.5 text-[8px] font-semibold bg-navy text-white rounded hover:bg-navy/80"
                      >
                        Record Invoice
                      </button>
                      <button
                        onClick={() => updateStatus(m.id, 'Awaiting')}
                        className="px-2 py-0.5 text-[8px] text-gray-500 border border-gray-200 rounded hover:bg-gray-50 hover:text-gray-700"
                        title="Move back to Awaiting"
                      >
                        ← Awaiting
                      </button>
                    </div>
                  ) : isInvoiced ? (
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => openMarkPaid(m)}
                        className="px-2 py-0.5 text-[8px] font-semibold bg-emerald-500 text-emerald-950 rounded hover:bg-emerald-600"
                      >
                        ✓ Mark Paid
                      </button>
                      <select
                        className="form-select text-[8px] py-0 px-1 min-w-[80px]"
                        value={m.status}
                        onChange={(e) => handleStatusChange(m, e.target.value)}
                      >
                        <option>Awaiting</option>
                        <option>Ready to bill</option>
                        <option>Invoiced</option>
                        <option>Paid</option>
                        <option>Cancelled</option>
                      </select>
                    </div>
                  ) : (
                    <select
                      className="form-select text-[8px] py-0 px-1 min-w-[80px]"
                      value={m.status}
                      onChange={(e) => handleStatusChange(m, e.target.value)}
                    >
                      <option>Awaiting</option>
                      <option>Ready to bill</option>
                      <option>Invoiced</option>
                      <option>Paid</option>
                      <option>Cancelled</option>
                    </select>
                  )}
                </td>
              </tr>
            );
          })}{sortedFiltered.length === 0 && <tr><td colSpan="9" className="text-center py-6 text-[10px] text-gray-400">No milestones found</td></tr>}</tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-2 px-1">
          <div className="text-[9px] text-gray-400">
            Showing {(safePage - 1) * PAGE_SIZE + 1}–{Math.min(safePage * PAGE_SIZE, sortedFiltered.length)} of {sortedFiltered.length}
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={safePage === 1}
              className="px-2 py-0.5 text-[9px] border border-gray-200 rounded bg-white disabled:opacity-40 hover:bg-gray-50"
            >‹ Prev</button>
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter(p => p === 1 || p === totalPages || Math.abs(p - safePage) <= 2)
              .reduce((acc, p, idx, arr) => {
                if (idx > 0 && p - arr[idx - 1] > 1) acc.push('…');
                acc.push(p);
                return acc;
              }, [])
              .map((p, i) =>
                p === '…'
                  ? <span key={`e${i}`} className="px-1 text-[9px] text-gray-400">…</span>
                  : <button key={p} onClick={() => setPage(p)}
                      className={`px-2 py-0.5 text-[9px] border rounded ${p === safePage ? 'bg-navy text-white border-gold' : 'border-gray-200 bg-white hover:bg-gray-50 text-gray-600'}`}
                    >{p}</button>
              )}
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={safePage === totalPages}
              className="px-2 py-0.5 text-[9px] border border-gray-200 rounded bg-white disabled:opacity-40 hover:bg-gray-50"
            >Next ›</button>
          </div>
        </div>
      )}
      </>}

      {/* SES Number Required Modal */}
      {sesPrompt && (
        <Modal
          title={`SES Number Required — ${sesPrompt.reference} / ${sesPrompt.milestone_code}`}
          onClose={() => setSesPrompt(null)}
          footer={
            <>
              <button onClick={() => setSesPrompt(null)} className="px-3 py-1 text-[10px] border border-gray-200 rounded bg-white">Cancel</button>
              <button
                onClick={submitSesAndReadyToBill}
                disabled={!sesInput.trim()}
                className="px-3 py-1 text-[10px] font-semibold bg-navy text-white rounded disabled:opacity-40"
              >
                Save SES &amp; Mark Ready to Bill
              </button>
            </>
          }
        >
          <div className="space-y-3">
            <div className="p-3 bg-amber-50 border border-amber-200 rounded flex gap-2 items-start">
              <svg className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
              <div className="text-[9px] text-amber-800">
                <div className="font-bold mb-0.5">SES Number Required</div>
                This SOW has <strong>SES Applicable</strong> set. A Service Entry Sheet number must be recorded before this milestone can be marked Ready to Bill.
              </div>
            </div>
            <div>
              <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">SES Number *</label>
              <input
                className="form-input"
                placeholder="e.g. 1234567890"
                value={sesInput}
                onChange={e => setSesInput(e.target.value)}
                autoFocus
                onKeyDown={e => { if (e.key === 'Enter' && sesInput.trim()) submitSesAndReadyToBill(); }}
              />
            </div>
            <div className="text-[8px] text-gray-400">{sesPrompt.description || sesPrompt.milestone_code}{seesMoney && <> · {formatMoney(sesPrompt.invoice_amount_excl)}</>}</div>
          </div>
        </Modal>
      )}

      {/* Mark as Paid Modal */}
      {markPaid && (
        <Modal
          title={`Mark as Paid — ${markPaid.reference} / ${markPaid.milestone_code}`}
          onClose={() => setMarkPaid(null)}
          footer={
            <>
              <button onClick={() => setMarkPaid(null)} className="px-3 py-1 text-[10px] border border-gray-200 rounded bg-white">Cancel</button>
              <button
                onClick={submitMarkPaid}
                disabled={!paidDate}
                className="px-3 py-1 text-[10px] font-semibold bg-emerald-500 text-emerald-950 rounded disabled:opacity-40"
              >
                Confirm Payment
              </button>
            </>
          }
        >
          <div className="space-y-3">
            <div className="p-3 bg-emerald-50 border border-emerald-200 rounded">
              <div className="text-[9px] text-emerald-700 font-semibold">{markPaid.description || markPaid.milestone_code}</div>
              {seesMoney && <div className="text-[11px] font-extrabold text-emerald-700 mt-1">{formatMoney(markPaid.invoice_amount_excl)} excl. VAT</div>}
              {markPaid.invoice_number && (
                <div className="text-[8px] text-emerald-600 mt-0.5">Invoice: {markPaid.invoice_number}</div>
              )}
            </div>
            <div>
              <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Payment Date *</label>
              <input
                className="form-input max-w-[180px]"
                type="date"
                value={paidDate}
                onChange={e => setPaidDate(e.target.value)}
                autoFocus
              />
            </div>
          </div>
        </Modal>
      )}

      {/* Record Invoice Modal */}
      {recordInvoice && (
        <Modal
          title={`Record Invoice — ${recordInvoice.reference} / ${recordInvoice.milestone_code}`}
          onClose={() => setRecordInvoice(null)}
          wide
          footer={
            <>
              <button onClick={() => setRecordInvoice(null)} className="px-3 py-1 text-[10px] border border-gray-200 rounded bg-white">Cancel</button>
              <button
                onClick={submitInvoice}
                disabled={!invoiceForm.invoice_number.trim() || !invoiceForm.formal_invoice_date}
                className="px-3 py-1 text-[10px] font-semibold bg-navy text-white rounded disabled:opacity-40"
              >
                Record &amp; Mark Invoiced
              </button>
            </>
          }
        >
          {/* Context panel */}
          <div className="mb-3 p-3 bg-gray-50 border border-gray-200 rounded grid grid-cols-2 gap-x-4 gap-y-1">
            {recordInvoice.client_name && (
              <div>
                <div className="text-[7px] text-gray-400 uppercase font-bold">Client</div>
                <div className="text-[9px] font-semibold text-gray-800">{recordInvoice.client_name}</div>
              </div>
            )}
            {recordInvoice.client_contact_name && (
              <div>
                <div className="text-[7px] text-gray-400 uppercase font-bold">Contact</div>
                <div className="text-[9px] text-gray-700">{recordInvoice.client_contact_name}</div>
              </div>
            )}
            {recordInvoice.sow_po_number && (
              <div>
                <div className="text-[7px] text-gray-400 uppercase font-bold">PO Number</div>
                <div className="text-[9px] font-mono text-gray-700">{recordInvoice.sow_po_number}</div>
              </div>
            )}
            {sesRelevant && recordInvoice.ses_number && (
              <div>
                <div className="text-[7px] text-gray-400 uppercase font-bold">SES Number</div>
                {/* select-all so the whole number can be lifted into the invoice in one click. */}
                <div className="text-[10px] font-mono font-bold text-navy select-all">{recordInvoice.ses_number}</div>
              </div>
            )}
            <div>
              <div className="text-[7px] text-gray-400 uppercase font-bold">Payment Terms</div>
              <div className="text-[9px] text-gray-700">{recordInvoice.payment_terms ? `${recordInvoice.payment_terms} days` : '30 days (default)'}</div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Invoice Number *</label>
              <input className="form-input" placeholder="e.g. INV-2024-001" value={invoiceForm.invoice_number} onChange={e => setF('invoice_number', e.target.value)} autoFocus />
            </div>
            <div>
              <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Invoice Date *</label>
              <input className="form-input" type="date" value={invoiceForm.formal_invoice_date} onChange={e => setF('formal_invoice_date', e.target.value)} />
            </div>
            {seesMoney && <>
            <div>
              <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Invoice Amount (excl. VAT) £</label>
              <input className="form-input" type="number" step="0.01" placeholder="0.00" value={invoiceForm.invoice_amount_excl} onChange={e => setF('invoice_amount_excl', e.target.value)} />
            </div>
            <div>
              <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Invoice Amount (incl. VAT) £</label>
              <input className="form-input" type="number" step="0.01" placeholder="0.00" value={invoiceForm.invoice_amount_incl} onChange={e => setF('invoice_amount_incl', e.target.value)} />
            </div>
            </>}
            {/* No number on record but the PO needs one. Capture it here rather than
                sending finance back to the milestone status flow mid-invoice. */}
            {sesRelevant && !recordInvoice.ses_number && (
              <div className="col-span-2">
                <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">SES Number</label>
                <input className="form-input font-mono" placeholder="Not recorded yet — enter the Service Entry Sheet number" value={invoiceForm.ses_number} onChange={e => setF('ses_number', e.target.value)} />
                <div className="text-[8px] text-amber-600 mt-0.5">This PO needs a Service Entry Sheet number on the invoice.</div>
              </div>
            )}
          </div>

          {previewPayDate && (
            <div className="mt-3 p-2 bg-blue-50 border border-blue-200 rounded flex items-center gap-2">
              <svg className="w-3.5 h-3.5 text-blue-500 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
              <div className="text-[9px] text-blue-700">
                <span className="font-bold">Expected Payment Date:</span> {formatDate(previewPayDate)}
                <span className="ml-1 text-blue-500">(Payment terms: {recordInvoice.payment_terms || 30} days)</span>
              </div>
            </div>
          )}
        </Modal>
      )}
    </div>
  );
}
